'use client';

import {
  Box,
  Button,
  Dialog,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Text,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import { IconRefresh, IconSearch, IconStar, type TablerIcon } from '@tabler/icons-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import {
  getAppNavigationSections,
  getBrandSlugFromPathname,
  getResolvedNavHref,
  WORKSPACE_NAVIGATION_ITEMS,
} from '@/components/core/app-layout/navigation';
import {
  TOP_NAV_FAVORITE_LABEL_OVERRIDES,
  useTopNavFavorites,
} from '@/components/core/app-layout/useTopNavFavorites';
import { useProject } from '@/contexts/ProjectContext';
import { isAiCmoBetaEnabled } from '@/lib/attune/growth/ai-cmo-feature';

type CommandAction = {
  label: string;
  href: string;
  icon: TablerIcon;
  keywords: string;
};

type CommandGroup = {
  label: string;
  actions: CommandAction[];
};

const APP_COMMAND_GROUPS: CommandGroup[] = getAppNavigationSections().map(section => ({
  label: section.title,
  actions: section.items.map(item => ({
    ...item,
    keywords: section.title,
  })),
}));

const COMMAND_GROUPS: CommandGroup[] = [
  {
    label: 'Workspace',
    actions: WORKSPACE_NAVIGATION_ITEMS.map(item => ({ ...item, keywords: 'Workspace' })),
  },
  ...APP_COMMAND_GROUPS,
];
const FAVORITABLE_ACTIONS = APP_COMMAND_GROUPS.filter(group => group.label !== 'Home').flatMap(
  group => group.actions
);
const FAVORITABLE_ACTION_HREFS = FAVORITABLE_ACTIONS.map(action => action.href);
const FAVORITABLE_ACTIONS_BY_HREF = new Map(
  FAVORITABLE_ACTIONS.map(action => [action.href, action])
);
const FAVORITABLE_ACTION_HREF_SET = new Set(FAVORITABLE_ACTION_HREFS);

const ICON_STROKE = 1.7;

export function CommandPalette() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { currentProject } = useProject();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const { favoriteHrefs, toggleFavorite, resetFavorites } =
    useTopNavFavorites(FAVORITABLE_ACTION_HREFS);
  const onboardingLocked =
    pathname === '/app/workspace' &&
    (searchParams.get('new_project') === 'true' || searchParams.get('onboarding_chat') === 'true');
  const aiCmoBetaEnabled = isAiCmoBetaEnabled(currentProject);

  const filteredGroups = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const favoriteHrefSet = new Set(favoriteHrefs);
    const isVisible = (action: CommandAction) =>
      action.href !== '/app/workspace?mode=growth-program' || aiCmoBetaEnabled;
    const matchesQuery = (action: CommandAction) =>
      !normalized || `${action.label} ${action.keywords}`.toLowerCase().includes(normalized);
    const starredActions = favoriteHrefs
      .map(href => FAVORITABLE_ACTIONS_BY_HREF.get(href))
      .filter((action): action is CommandAction => Boolean(action))
      .map(action => ({
        ...action,
        label: TOP_NAV_FAVORITE_LABEL_OVERRIDES[action.href] ?? action.label,
      }))
      .filter(action => isVisible(action) && matchesQuery(action));
    const regularGroups = COMMAND_GROUPS.map(group => ({
      ...group,
      actions: group.actions.filter(action => {
        return isVisible(action) && matchesQuery(action) && !favoriteHrefSet.has(action.href);
      }),
    })).filter(group => group.actions.length > 0);

    return [
      ...(starredActions.length > 0 ? [{ label: 'Starred', actions: starredActions }] : []),
      ...regularGroups,
    ];
  }, [aiCmoBetaEnabled, favoriteHrefs, query]);

  const filteredActions = useMemo(
    () => filteredGroups.flatMap(group => group.actions),
    [filteredGroups]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const commandKey = event.metaKey || event.ctrlKey;
      if (!commandKey) return;

      const key = event.key.toLowerCase();

      if (key === 'k') {
        event.preventDefault();
        if (onboardingLocked) {
          setOpen(false);
          return;
        }
        setOpen(current => {
          if (current) {
            setQuery('');
            setActiveIndex(0);
          }
          return !current;
        });
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onboardingLocked]);

  useEffect(() => {
    if (!onboardingLocked) return;
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, [onboardingLocked]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const runAction = (action: CommandAction) => {
    close();
    const brandSlug =
      currentProject?.brand_settings?.brand_slug ?? getBrandSlugFromPathname(pathname);
    router.push(getResolvedNavHref({ href: action.href, brandSlug, searchParams, pathname }));
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredActions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(current => (current + 1) % filteredActions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(current => (current - 1 + filteredActions.length) % filteredActions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runAction(filteredActions[activeIndex] ?? filteredActions[0]);
    }
  };

  let actionIndex = 0;

  return (
    <Dialog.Root
      open={open}
      placement="center"
      onOpenChange={details => {
        setOpen(details.open);
        if (!details.open) close();
      }}
    >
      <Portal>
        <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(5px)" />
        <Dialog.Positioner
          p={{ base: '3', md: '6' }}
          pt={{ base: '3', md: '12vh' }}
          alignItems="flex-start"
        >
          <Dialog.Content
            layerStyle="surfacePopover"
            borderColor="ui.border.subtle"
            borderRadius="glassCard"
            boxShadow="var(--attune-shadow-floating, none)"
            p="0"
            maxW="540px"
            w="full"
            h="auto"
            maxH={{ base: 'calc(100dvh - 24px)', md: 'min(520px, 72dvh)' }}
            overflow="hidden"
          >
            <VisuallyHidden>
              <Dialog.Title>Attune command menu</Dialog.Title>
            </VisuallyHidden>

            <Dialog.Header
              px="3"
              py="2"
              minH="48px"
              borderBottomWidth="1px"
              borderColor="ui.border.subtle"
            >
              <Flex align="center" w="full" minW={0} gap="2.5">
                <Icon boxSize="4" color="ui.text.tertiary" flexShrink={0} aria-hidden="true">
                  <IconSearch size="100%" stroke={ICON_STROKE} />
                </Icon>
                <Input
                  name="attune-command-search"
                  autoComplete="off"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Search commands"
                  aria-label="Search commands"
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="attune-command-results"
                  aria-activedescendant={
                    filteredActions.length > 0 ? `attune-command-${activeIndex}` : undefined
                  }
                  autoFocus
                  border="0"
                  px="0"
                  minH="8"
                  h="8"
                  bg="transparent"
                  color="ui.text.primary"
                  caretColor="ui.text.primary"
                  fontSize="sm"
                  fontWeight="450"
                  letterSpacing="-0.015em"
                  _placeholder={{ color: 'ui.text.tertiary', opacity: 1 }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                />
                <Box
                  as="kbd"
                  px="1.5"
                  py="0.5"
                  borderWidth="1px"
                  borderColor="ui.border.subtle"
                  borderRadius="6px"
                  bg="ui.surface.input"
                  color="ui.text.tertiary"
                  fontSize="10px"
                  fontWeight="650"
                  lineHeight="1.4"
                  flexShrink={0}
                >
                  ⌘K
                </Box>
              </Flex>
            </Dialog.Header>

            <Dialog.Body
              id="attune-command-results"
              role="listbox"
              aria-label="Commands"
              px="1.5"
              py="1"
              overflowY="auto"
            >
              {filteredGroups.length > 0 ? (
                <VStack align="stretch" gap="0">
                  {filteredGroups.map((group, groupIndex) => (
                    <Box key={group.label}>
                      <Text
                        px="2"
                        pt={groupIndex === 0 ? '1' : '2'}
                        pb="1"
                        color="ui.text.tertiary"
                        fontSize="10px"
                        fontWeight="700"
                        lineHeight="1"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                      >
                        {group.label}
                      </Text>
                      <VStack align="stretch" gap="0">
                        {group.actions.map(action => {
                          const currentIndex = actionIndex++;
                          const ActionIcon = action.icon;
                          const active = currentIndex === activeIndex;
                          const canFavorite = FAVORITABLE_ACTION_HREF_SET.has(action.href);
                          const isFavorite = favoriteHrefs.includes(action.href);

                          return (
                            <HStack
                              key={action.href}
                              gap="0.5"
                              minW={0}
                              onMouseEnter={() => setActiveIndex(currentIndex)}
                            >
                              <Button
                                id={`attune-command-${currentIndex}`}
                                role="option"
                                aria-selected={active}
                                variant="ghost"
                                h="8"
                                minH="8"
                                minW={0}
                                flex="1"
                                px="2"
                                borderRadius="glassInput"
                                justifyContent="flex-start"
                                bg={active ? 'ui.surface.selected' : 'transparent'}
                                color="ui.text.secondary"
                                transition="background-color 120ms ease, color 120ms ease"
                                _hover={{ bg: 'ui.surface.rowHover', color: 'ui.text.primary' }}
                                _focusVisible={{
                                  bg: 'ui.surface.selected',
                                  color: 'ui.text.primary',
                                  outline: '2px solid',
                                  outlineColor: 'ui.border.accent',
                                  outlineOffset: '-2px',
                                }}
                                onClick={() => runAction(action)}
                              >
                                <Flex align="center" w="full" minW={0} gap="2.5">
                                  <Icon
                                    boxSize="3.5"
                                    color="ui.text.secondary"
                                    flexShrink={0}
                                    aria-hidden="true"
                                  >
                                    <ActionIcon size="100%" stroke={ICON_STROKE} />
                                  </Icon>
                                  <Text
                                    flex="1"
                                    minW={0}
                                    textAlign="start"
                                    truncate
                                    fontSize="13px"
                                    fontWeight="550"
                                    letterSpacing="-0.01em"
                                    color={active ? 'ui.text.primary' : 'ui.text.secondary'}
                                  >
                                    {action.label}
                                  </Text>
                                </Flex>
                              </Button>
                              {canFavorite ? (
                                <IconButton
                                  aria-label={`${isFavorite ? 'Remove' : 'Add'} ${
                                    action.label
                                  } ${isFavorite ? 'from' : 'to'} favorites`}
                                  aria-pressed={isFavorite}
                                  variant="ghost"
                                  size="xs"
                                  w="8"
                                  h="8"
                                  minW="8"
                                  flexShrink={0}
                                  borderRadius="glassInput"
                                  color={isFavorite ? 'accent.fg' : 'ui.text.tertiary'}
                                  onClick={() => toggleFavorite(action.href)}
                                  _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
                                  _focusVisible={{
                                    outline: '2px solid',
                                    outlineColor: 'ui.border.accent',
                                    outlineOffset: '-2px',
                                  }}
                                >
                                  <IconStar
                                    size={14}
                                    stroke={1.8}
                                    fill={isFavorite ? 'currentColor' : 'none'}
                                    aria-hidden="true"
                                  />
                                </IconButton>
                              ) : null}
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <VStack py="8" gap="1">
                  <Text fontSize="sm" fontWeight="650" color="ui.text.primary">
                    No matching command
                  </Text>
                  <Text fontSize="xs" color="ui.text.secondary">
                    Try a page, task, or workspace keyword.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Flex
              minH="34px"
              px="3"
              align="center"
              justify="space-between"
              borderTopWidth="1px"
              borderColor="ui.border.subtle"
              color="ui.text.tertiary"
            >
              <HStack gap="3">
                <Text fontSize="10px">
                  <Text as="span" fontWeight="700" color="ui.text.secondary">
                    ↑↓
                  </Text>{' '}
                  Navigate
                </Text>
                <Text fontSize="10px">
                  <Text as="span" fontWeight="700" color="ui.text.secondary">
                    ↵
                  </Text>{' '}
                  Open
                </Text>
              </HStack>
              <HStack gap="2.5">
                <Button
                  size="xs"
                  variant="ghost"
                  h="7"
                  minW={0}
                  px="1.5"
                  gap="1"
                  color="ui.text.tertiary"
                  fontSize="10px"
                  fontWeight="600"
                  onClick={resetFavorites}
                  _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary' }}
                >
                  <IconRefresh size={12} aria-hidden="true" />
                  Default
                </Button>
                <Text fontSize="10px">
                  <Text as="span" fontWeight="700" color="ui.text.secondary">
                    Esc
                  </Text>{' '}
                  Close
                </Text>
              </HStack>
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
